import { CompanyDto, CompanyQueryType, CompanyTypeFilter, CreateCompanyDto, UpdateCompanyDto } from '@sr/dto';
import { authorizedRequest } from 'shared/api/axios';
import { COMPANY_BASE_URL } from 'shared/api/api';

import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery } from 'shared/api/rtk-query';
import { BackendSuffixes, companiesListApi } from 'features/company/company-list.api';

const BASE_URL = COMPANY_BASE_URL;

export const companyApi = createApi({
  reducerPath: 'company',
  baseQuery: createBaseQuery(),
  endpoints: (builder) => ({
    getCompanyDetails: builder.query<CompanyDto, number>({
      query: (companyId) => `${COMPANY_BASE_URL}/${companyId}`,
      providesTags: (result, error, companyId) => [{ type: 'Company', companyId }]
    }),
    updateCompanyDetails: builder.mutation<CompanyDto, UpdateCompanyDto>({
      query: (dto) => (
        {
          url: `${COMPANY_BASE_URL}`,
          body: dto,
          method: 'PUT'
        }),
      invalidatesTags: (result, error, dto) => [{ type: 'Company', id: dto.id }]
    }),
    postCompany: builder.mutation<CompanyDto, CreateCompanyDto>({
      query: (dto) => (
        {
          url: `${COMPANY_BASE_URL}`,
          body: dto,
          method: 'POST'
        }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(companiesListApi.util.invalidateTags(
            Object.values(BackendSuffixes).map((id) => ({ type: 'companies-list' as const, id }))
          ));
        } catch (e) {
          console.error('RTK Query onQueryStarted error', e);
        }
      },
      invalidatesTags: (result, error, dto) => [{ type: 'Company' }]
    })
  }),
  tagTypes: ['Company']
});

const COMPANIES_DROPDOWN_LIMIT = 25;

export const fetchCompaniesDropdown = async (name = '', legalEntityId?: number, companyType?: CompanyTypeFilter, limit = COMPANIES_DROPDOWN_LIMIT) => {
  const legalEntityIdQuery = legalEntityId ? '&legalEntityId=' + String(legalEntityId) : '';
  const companyTypeQuery = companyType ? '&isSupplier=' + String(companyType.isSupplier)+'&isClient=' + String(companyType.isClient)+'&isMedia=' + String(companyType.isMedia)+'&isCompetitor=' + String(companyType.isCompetitor) : '';
  return authorizedRequest<void, CompanyDto[]>(`${BASE_URL}/dropdown/?name=${name}${legalEntityIdQuery}${companyTypeQuery}&limit=${limit}`, 'GET');
};

export const fetchClientsIds = async (filter: CompanyQueryType) => {
  return authorizedRequest<void, number[]>(`${BASE_URL}/clients-ids?type=${filter}`, 'GET');
};

export const unlinkManagers = async (companiesIds: number[], queryType: CompanyQueryType) => {
  return authorizedRequest<void, any>(`${BASE_URL}/unlinkManagers?companiesIds=${companiesIds}&queryType=${queryType}`, 'DELETE');
};

export const blockManagers = async (companiesIds: number[], queryType: CompanyQueryType) => {
  return authorizedRequest<void, any>(`${BASE_URL}/blockManagers?companiesIds=${companiesIds}&queryType=${queryType}`, 'PUT');
};

export const fetchOurCompanyId = async () => {
  return authorizedRequest<void, number>(`${BASE_URL}/our-company`, 'GET');
};
